import { useEffect, useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button } from '@wajeez-web/components/ui';
import { Content, Server } from '@wajeez-web/core/api-base';
import {
  NarratorActions,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { useAppDispatch } from '@wajeez-web/stores/base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { PlusIcon } from '@heroicons/react/solid';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { DeleteItemAlert } from '@wajeez-web/components/ui';
import { useTranslation } from 'react-i18next';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';

import NarratorssModal from '../components/NarratorModal';
import { DataTableWrapper } from '@wajeez-web/components/helpers';

interface NarratorDataTableExtended extends Server.NarratorDatatable {
  totalCount: number | undefined;
}

const NarratorsPage = () => {
  const dispatch = useAppDispatch();

  const [isNarratorModalOpen, setIsNarratorModalOpen] = useState(false);
  const [selectedNarratorId, setSelectedNarratorId] = useState<
    number | undefined
  >(undefined);

  const {
    tablePreferances,
    setTablePreferences,
    getData,
    isTableReferencesLoading,
  } = HandleTableData(
    'narrators',
    NarratorActions.getNarratorsAsDataTableAsync
  );

  const { dataTableData, datatableStatus } = useContentSelector(
    (state) => state.narrators
  );

  const dataTableDataExtended = dataTableData as NarratorDataTableExtended;

  const { t } = useTranslation();

  return (
    <div className="h-full">
      <PageLayoutsContainer
        title={`Narrators`}
        withoutRightPadding={false}
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add New Narrator'}
                Icon={PlusIcon}
                onClick={() => {
                  setIsNarratorModalOpen(true);
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [AdminSystemPermissions.narrators.add],
          },
        ]}
        breadCrumbsLinks={[{ title: 'Narrators' }]}
      >
        <DataTableWrapper
          data={dataTableDataExtended as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          isLoading={datatableStatus === 'loading'}
          rowActions={(row) => {
            return [
              {
                title: 'Edit',
                permission: AdminSystemPermissions.narrators.edit,
                endpoint: '',
                type: 'request',
                action: () => {
                  setSelectedNarratorId(Number(row[1]));
                  setIsNarratorModalOpen(true);
                },
              },
              {
                title: 'Delete',
                permission: AdminSystemPermissions.narrators.delete,
                endpoint: '',
                type: 'request',
                action: () => {
                  DeleteItemAlert(() => {
                    return dispatch(
                      NarratorActions.deleteNarratorAsync(
                        parseInt(row[1] as string)
                      )
                    ).then((res) => {
                      if (res.meta.requestStatus === 'fulfilled') {
                        getData();
                      }
                    });
                  }, t('general.will delete') + row[2]);
                },
              },
            ];
          }}
          columnsSelectors={(r) => {
            return {
              '2': (
                <div
                  className="text-[#38A7B4] font-medium cursor-pointer"
                  onClick={() => {
                    setSelectedNarratorId(Number(r[1]));
                    setIsNarratorModalOpen(true);
                  }}
                >
                  {r[2]}
                </div>
              ),
            };
          }}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      <NarratorssModal
        isOpen={isNarratorModalOpen}
        onClose={() => {
          setIsNarratorModalOpen(false);
          setSelectedNarratorId(undefined);
        }}
        onSubmit={() => {
          getData();

          setIsNarratorModalOpen(false);
          setSelectedNarratorId(undefined);
        }}
        id={selectedNarratorId}
      />
    </div>
  );
};

export default NarratorsPage;
